import React, { useCallback, useEffect, useRef, useState } from 'react';
import MentionInput from './MentionInput';
import { resolveMusicMentions, enrichOrderedMentions } from '../mentionUtils';
import type { MusicMentionData } from '../mentionUtils';
import styled from 'styled-components';
import { useAuth } from '../context/auth';
import Post from '../types/Post';
import api from '../api';

const CommentButton = styled.button`
  background: white;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  color: black;
  font-weight: 500;
  font-size: 0.75rem;
  cursor: pointer;
  margin-top: 0.25em;
`;

const CancelButton = styled.button`
  background: none;
  border: none;
  padding: 0.5rem 1rem;
  color: #666;
  font-size: 0.75rem;
  cursor: pointer;
  margin-top: 0.25em;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.25em;
`;

const ReplyPlaceholder = styled.div<{ $noDivider?: boolean }>`
  color: #999;
  font-size: 0.85rem;
  padding: 0.6em 0.5em;
  cursor: text;
  border-top: ${(p) => (p.$noDivider ? 'none' : '1px solid #eee')};

  &:hover {
    color: #666;
  }
`;

interface CreateCommentProps {
  post: Post;
  onCommentAdded: (comment: any) => void;
  parentCommentId?: string;
  onCancel?: () => void;
  initialText?: string;
  autoFocus?: boolean;
  // No divider above the collapsed "Reply..." when nothing sits above it,
  // e.g. the first thing in a comments card of its own.
  noDivider?: boolean;
}

export default function CreateComment(props: CreateCommentProps) {
  const [commentText, setCommentText] = useState(props.initialText || '');
  const [expanded, setExpanded] = useState(!!props.autoFocus);
  const [musicMentions, setMusicMentions] = useState<MusicMentionData[]>([]);
  const getSubmitTextRef = useRef<(() => { text: string; mentions: any[] }) | null>(null);
  const { authTokens } = useAuth();
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(
    function () {
      if (props.autoFocus && !expanded) {
        setExpanded(true);
      }
    },
    [props.autoFocus]
  );

  // Scroll composer into view after it expands.
  // Wait for the keyboard to finish appearing before scrolling.
  useEffect(
    function () {
      if (!expanded) return;
      if (!wrapperRef.current) return;

      var el = wrapperRef.current;
      var settled = false;

      if (window.visualViewport) {
        // Listen for the viewport to stop resizing (keyboard finished appearing)
        var timer: any = null;
        function onResize() {
          if (timer) clearTimeout(timer);
          timer = setTimeout(function () {
            if (!settled) {
              settled = true;
              el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          }, 150);
        }
        window.visualViewport.addEventListener('resize', onResize);
        // Fallback: if no resize event (desktop, or keyboard already open), scroll after a short delay
        var fallback = setTimeout(function () {
          if (!settled) {
            settled = true;
            el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }, 300);
        return function () {
          window.visualViewport!.removeEventListener('resize', onResize);
          if (timer) clearTimeout(timer);
          clearTimeout(fallback);
        };
      } else {
        // No visualViewport API -- just delay
        var delay = setTimeout(function () {
          el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 300);
        return function () {
          clearTimeout(delay);
        };
      }
    },
    [expanded]
  );

  const isTopLevel = !props.parentCommentId;

  const handleComment = useCallback(() => {
    if (!commentText.trim()) return;

    const submitData = getSubmitTextRef.current ? getSubmitTextRef.current() : null;
    const finalText = (submitData ? submitData.text : null) || commentText;
    const orderedMentions = submitData
      ? enrichOrderedMentions(submitData.mentions, musicMentions)
      : musicMentions;

    resolveMusicMentions(orderedMentions).then(function (resolved) {
      var request: any = {
        user: { id: authTokens.user.id },
        post: { id: props.post.id },
        text: finalText,
      };
      if (props.parentCommentId) {
        request.parentComment = { id: props.parentCommentId };
      }
      if (resolved.length > 0) {
        request.musicMentions = resolved;
      }
      api
        .post('/api/comments', request)
        .then(function (result: any) {
          setCommentText('');
          setMusicMentions([]);
          if (isTopLevel) {
            setExpanded(false);
          }
          if (props.onCommentAdded) {
            props.onCommentAdded({
              id: result.data.id || Date.now().toString(),
              text: result.data.text || finalText,
              user: authTokens.user,
              post: props.post,
              parentCommentId: props.parentCommentId || undefined,
              replies: [],
              musicMentions: resolved.map(function (md, idx) {
                return {
                  id: '',
                  mediaId: md.mediaId || null,
                  title: md.title,
                  artistName: md.artistName,
                  type: md.type,
                  placeholderIndex: idx,
                };
              }),
            });
          }
          if (props.onCancel) {
            props.onCancel();
          }
        })
        .catch(function () {
          window.alert('Failed to post comment.');
        });
    });
  }, [commentText, authTokens, props, isTopLevel, musicMentions]);

  if (authTokens == undefined || authTokens.user == undefined) {
    return <></>;
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleComment();
    }
  }

  function handleCancel() {
    setCommentText('');
    if (isTopLevel) {
      setExpanded(false);
    }
    if (props.onCancel) {
      props.onCancel();
    }
  }

  // Top-level collapsed placeholder
  if (isTopLevel && !expanded) {
    return (
      <ReplyPlaceholder
        $noDivider={props.noDivider}
        onClick={function () {
          setExpanded(true);
        }}
      >
        Reply...
      </ReplyPlaceholder>
    );
  }

  return (
    <div ref={wrapperRef}>
      <MentionInput
        name="commentText"
        value={commentText}
        onChange={(e: any) => setCommentText(e.target.value)}
        onKeyDown={handleKeyDown}
        musicMentions={musicMentions}
        onMusicMentionsChange={setMusicMentions}
        getSubmitTextRef={getSubmitTextRef}
        minRows={props.parentCommentId ? 2 : 3}
        placeholder={props.parentCommentId ? 'Write a reply...' : undefined}
        autoFocus={(isTopLevel || props.autoFocus) && expanded}
        style={{ width: '100%', boxSizing: 'border-box' }}
      />
      <ButtonRow>
        <CommentButton onClick={handleComment}>
          {props.parentCommentId ? 'Reply' : 'Comment'}
        </CommentButton>
        <CancelButton onClick={handleCancel}>Cancel</CancelButton>
      </ButtonRow>
    </div>
  );
}
